/**
 * Phase 1E.5 — Public Album Page.
 *
 * Read-only, no auth. Loads a public album via
 * GET /api/public/collectors/:username/albums/:album_slug and renders:
 *  - Header with collector avatar + display name + username
 *  - Album header (cover, manufacturer, release, variant, nickname, mode, status)
 *  - Completion header (huge %, progress bar, Completed/Near-Completion badge, 5 tiny stats)
 *  - Category breakdown grid (live from completion engine)
 *  - Tabbed lists: Overview · Missing · Duplicates (search + category filter + Copy)
 *  - Web Share API when supported; Copy link fallback.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Copy, Share2, Search, Loader2, User, Sparkles,
  CheckCircle2, Album as AlbumIcon,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import SEO from "@/components/SEO";
import { track, EVENT } from "@/lib/analytics";
import { resolveAlbumSubtitle } from "@/lib/albumTitle";
import { collectionMethodLabelFor } from "@/lib/albumPresentation";

const TS_TONE = {
  OWNED:     "border-emerald-300 bg-emerald-50 text-emerald-900",
  DUPLICATE: "border-blue-300    bg-blue-50    text-blue-900",
  MISSING:   "border-red-300     bg-red-50     text-red-900",
  UNKNOWN:   "border-neutral-300 bg-neutral-50 text-neutral-800",
};

export default function PublicAlbum() {
  const { username, album_slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState("overview");
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("");

  useEffect(() => {
    let ignore = false;
    track(EVENT.PUBLIC_ALBUM_VIEWED, { username, album_slug });
    api.get(`/public/collectors/${username}/albums/${album_slug}`)
      .then(r => { if (!ignore) setData(r.data); })
      .catch(e => { if (!ignore) setErr(e?.response?.status || 500); });
    return () => { ignore = true; };
  }, [username, album_slug]);

  // Category options for filters (from both lists). MUST be called before early returns.
  const catOptions = useMemo(() => {
    const s = new Set();
    const lists = data?.lists;
    if (!lists) return [];
    for (const src of [lists.missing, lists.duplicates]) {
      for (const item of (src || [])) {
        if (item.category?.code) s.add(item.category.code);
      }
    }
    return [...s].sort();
  }, [data]);

  if (err === 404) return <NotFound />;
  if (err) return <NotFound message="Something went wrong." />;
  if (!data) return (
    <div className="min-h-screen grid place-items-center" data-testid="pa-loading">
      <Loader2 className="animate-spin" />
    </div>
  );

  const { collector, album, catalog, completion, lists } = data;
  // Iter Catalog-Cover-Meta — backend now publishes normalised `cover_image_url`
  // (thumbnail preferred) on the variant and release blocks. We still guard
  // against legacy servers by falling back to raw string `cover_image` values.
  const rawVariantCover = catalog?.album_variant?.cover_image;
  const rawReleaseCover = catalog?.release?.cover_image;
  const cover = catalog?.album_variant?.cover_image_url
             || catalog?.release?.cover_image_url
             || (typeof rawVariantCover === "string" ? rawVariantCover : null)
             || (typeof rawReleaseCover === "string" ? rawReleaseCover : null);

  const filterFn = (row) => {
    if (catFilter && row.category?.code !== catFilter) return false;
    if (!q.trim()) return true;
    const qq = q.trim().toLowerCase();
    return (
      (row.sticker_number || "").toLowerCase().includes(qq) ||
      (row.name || "").toLowerCase().includes(qq) ||
      (row.player_last_name || "").toLowerCase().includes(qq) ||
      (row.player_first_name || "").toLowerCase().includes(qq)
    );
  };
  const missingFiltered = lists.missing.filter(filterFn);
  const duplicatesFiltered = lists.duplicates.filter(filterFn);

  const publicUrl = `${window.location.origin}/c/${collector.username}/albums/${album.public_slug}`;

  const share = async () => {
    const title = `${collector.display_name} · ${catalog.album_variant?.name}`;
    const text = `${completion.percentage}% complete · ${catalog.release?.name}`;
    if (navigator.share) {
      try { await navigator.share({ title, text, url: publicUrl }); } catch {}
    } else {
      await navigator.clipboard.writeText(publicUrl);
      toast.success("Public link copied to clipboard.");
    }
  };
  const copyLink = async () => {
    await navigator.clipboard.writeText(publicUrl);
    toast.success("Public link copied.");
  };
  const copyMissing = async () => {
    const list = missingFiltered.map(x => x.sticker_number).filter(Boolean).join("\n");
    await navigator.clipboard.writeText(list);
    toast.success(`Copied ${missingFiltered.length} missing sticker code${missingFiltered.length === 1 ? "" : "s"}.`);
  };
  const copyDupes = async () => {
    // Repeat each code by its extra_quantity so paste-back is round-trip friendly.
    const list = duplicatesFiltered.flatMap(x =>
      Array(Math.max(1, x.extra_quantity || 0)).fill(x.sticker_number)
    ).filter(Boolean).join("\n");
    await navigator.clipboard.writeText(list);
    toast.success(`Copied ${duplicatesFiltered.length} duplicate sticker${duplicatesFiltered.length === 1 ? "" : "s"}.`);
  };

  const seoTitle = `${collector.display_name} · ${catalog.album_variant?.name} · ${completion.percentage}% · CollectorUp`;

  return (
    <main className="min-h-screen bg-[var(--cream)] pb-16" data-testid="public-album">
      <SEO title={seoTitle}
           description={`${catalog.manufacturer?.name} · ${catalog.release?.name} · ${completion.percentage}% complete.`} />

      {/* Collector strip */}
      <header className="border-b-2 border-[var(--ink)] bg-white">
        <div className="max-w-5xl mx-auto p-4 flex items-center gap-3">
          <Link to={`/collectors/${collector.username}`} className="flex items-center gap-3 group"
                data-testid="pa-collector-link">
            <div className="w-12 h-12 rounded-full border-2 border-[var(--ink)] bg-[var(--panini-yellow-soft)] grid place-items-center overflow-hidden">
              {collector.avatar_url
                ? <img src={collector.avatar_url} alt="" className="w-full h-full object-cover" />
                : <User size={20} />}
            </div>
            <div>
              <div className="font-display text-lg leading-none group-hover:underline"
                   data-testid="pa-collector-name">
                {collector.display_name}
              </div>
              <div className="text-xs font-mono text-[var(--smoke)]"
                   data-testid="pa-collector-username">
                @{collector.username}
              </div>
            </div>
          </Link>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={share}
                    className="border-2 border-[var(--ink)] rounded-full px-3 py-2 text-xs font-bold uppercase tracking-widest bg-white hover:bg-neutral-100 flex items-center gap-1"
                    data-testid="pa-share">
              <Share2 size={14} /> Share
            </button>
            <button onClick={copyLink}
                    className="border-2 border-[var(--ink)] rounded-full px-3 py-2 text-xs font-bold uppercase tracking-widest bg-white hover:bg-neutral-100 flex items-center gap-1"
                    data-testid="pa-copy-link">
              <Copy size={14} /> Copy link
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto p-4 space-y-6">
        {/* Album header card */}
        <section className="border-2 border-[var(--ink)] rounded-2xl bg-white overflow-hidden shadow-[4px_4px_0_var(--ink)]"
                 data-testid="pa-album-header">
          {/* Iter QA #005 — contain-fit on neutral bg for consistent
              hero preview regardless of upload aspect ratio. */}
          <div className="aspect-[4/3] bg-neutral-50 relative flex items-center justify-center">
            {cover ? (
              <img src={cover} alt=""
                   className="max-w-full max-h-full w-auto h-auto object-contain object-center" />
            ) : (
              <div className="w-full h-full grid place-items-center text-[var(--smoke)]">
                <AlbumIcon size={48} />
              </div>
            )}
          </div>
          <div className="p-5">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--smoke)]">
              {catalog.manufacturer?.name} · {catalog.collection_family?.name}
              {catalog.release?.year ? ` · ${catalog.release.year}` : ""}
            </div>
            <h1 className="font-display text-2xl md:text-3xl mt-1" data-testid="pa-album-title">
              {catalog.album_variant?.name}
            </h1>
            {(() => {
              // Iter Album-Title-Swap — suppress generic / echoed nicknames.
              const sub = resolveAlbumSubtitle(album.nickname, catalog.album_variant?.name);
              return sub ? (
                <div className="text-sm italic text-[var(--smoke)] mt-1" data-testid="pa-album-nickname">
                  &ldquo;{sub}&rdquo;
                </div>
              ) : null;
            })()}
            <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-1 text-xs mt-4">
              <Meta k="Release" v={catalog.release?.name} />
              <Meta k="Sticker mode" v={album.sticker_mode?.name} />
              {collectionMethodLabelFor(album) && (
                <Meta k="Method" v={collectionMethodLabelFor(album)} />
              )}
              <Meta k="Status" v={{"in_progress":"In Progress","completed":"Completed"}[album.status] || album.status} />
              <Meta k="Base stickers" v={catalog.base_sticker_count} />
              <Meta k="Promotional" v={catalog.promotional_sticker_count} />
              <Meta k="Total required" v={catalog.total_required} />
            </dl>
          </div>
        </section>

        {/* Completion header */}
        <CompletionHeader completion={completion} />

        {/* Category breakdown */}
        {completion.category_breakdown?.length > 0 && (
          <CategoryBreakdown categories={completion.category_breakdown} />
        )}

        {/* Tabs */}
        <div className="flex gap-1 border-b-2 border-[var(--ink)]" role="tablist"
             data-testid="pa-tabs">
          {[
            ["overview",   "Overview",   completion.total],
            ["missing",    "Missing",    completion.missing],
            ["duplicates", "Duplicates", completion.duplicate],
          ].map(([id, label, n]) => (
            <button key={id}
                    role="tab"
                    aria-selected={tab === id}
                    onClick={() => setTab(id)}
                    data-testid={`pa-tab-${id}`}
                    className={"px-4 py-2 text-sm font-bold uppercase tracking-widest " +
                      (tab === id
                        ? "bg-[var(--ink)] text-white"
                        : "bg-white text-[var(--ink)] hover:bg-neutral-100")}>
              {label} · <span className="font-mono">{n}</span>
            </button>
          ))}
        </div>

        {tab === "overview" && (
          <OverviewTab completion={completion} />
        )}
        {tab === "missing" && (
          <ListTab
            testid="pa-missing-tab" title="Missing stickers"
            rows={missingFiltered}
            emptyText={completion.is_complete
              ? "This album is complete."
              : (completion.missing === 0
                  ? "No stickers are currently marked missing."
                  : "No missing stickers match the current filters.")}
            q={q} setQ={setQ}
            catOptions={catOptions} catFilter={catFilter} setCatFilter={setCatFilter}
            onCopy={copyMissing} copyLabel="Copy Missing List"
            renderExtra={null} />
        )}
        {tab === "duplicates" && (
          <ListTab
            testid="pa-duplicates-tab" title="Duplicate stickers"
            rows={duplicatesFiltered}
            emptyText={completion.duplicate === 0
              ? "No duplicate stickers listed."
              : "No duplicates match the current filters."}
            q={q} setQ={setQ}
            catOptions={catOptions} catFilter={catFilter} setCatFilter={setCatFilter}
            onCopy={copyDupes} copyLabel="Copy Duplicate List"
            renderExtra={(row) => (
              <span className="ml-2 text-xs font-mono text-blue-800">
                +{row.extra_quantity} extra
              </span>
            )} />
        )}
      </div>
    </main>
  );
}

// ═══════════════════════════════════════════════════════════════════
function CompletionHeader({ completion }) {
  const pct = completion.percentage || 0;
  const bar = pct >= 100 ? "bg-emerald-500"
            : pct >= 95   ? "bg-emerald-400"
            : pct >= 50   ? "bg-[var(--panini-yellow)]"
            : pct > 0     ? "bg-orange-400"
            : "bg-neutral-300";
  return (
    <section className="border-2 border-[var(--ink)] rounded-2xl bg-white p-5 shadow-[4px_4px_0_var(--ink)]"
             data-testid="pa-completion-header">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)]">Completion</div>
          <div className="text-5xl font-display" data-testid="pa-completion-pct">
            {pct}<span className="text-2xl">%</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {completion.is_complete && (
            <span className="px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-widest bg-emerald-600 text-white"
                  data-testid="pa-badge-complete">
              <CheckCircle2 size={12} className="inline mr-1" /> Completed
            </span>
          )}
          {!completion.is_complete && completion.is_near_completion && (
            <span className="px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-widest border-2 border-emerald-500 text-emerald-900 bg-emerald-50"
                  data-testid="pa-badge-near">
              <Sparkles size={12} className="inline mr-1" /> Near completion
            </span>
          )}
        </div>
      </div>
      <div className="h-3 mt-3 bg-neutral-200 rounded-full overflow-hidden"
           role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}
           data-testid="pa-progress-bar">
        <div className={"h-full " + bar}
             style={{width: `${Math.min(100, Math.max(0, pct))}%`}} />
      </div>
      {completion.is_complete && (
        <div className="mt-4 border-2 border-emerald-500 rounded-xl bg-emerald-50 p-4 text-center"
             data-testid="pa-completed-panel">
          <div className="font-display text-xl text-emerald-900">Album Completed</div>
          <div className="text-sm text-emerald-800">All required stickers collected.</div>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-4">
        <TinyStat label="Owned"     value={completion.owned}     tone="OWNED"     testid="pa-comp-owned" />
        <TinyStat label="Duplicate" value={completion.duplicate} tone="DUPLICATE" testid="pa-comp-duplicate" />
        <TinyStat label="Missing"   value={completion.missing}   tone="MISSING"   testid="pa-comp-missing" />
        <TinyStat label="Unknown"   value={completion.unknown}   tone="UNKNOWN"   testid="pa-comp-unknown" />
        <TinyStat label="Remaining" value={completion.remaining} tone="UNKNOWN"   testid="pa-comp-remaining" />
      </div>
    </section>
  );
}

function CategoryBreakdown({ categories }) {
  return (
    <section className="border-2 border-[var(--ink)] rounded-2xl bg-white p-4"
             data-testid="pa-category-breakdown">
      <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)] mb-3">
        Category breakdown
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {categories.map(c => {
          const pct = c.completion_percentage || 0;
          const bar = pct >= 100 ? "bg-emerald-500"
                    : pct >= 50   ? "bg-[var(--panini-yellow)]"
                    : pct > 0     ? "bg-orange-400" : "bg-neutral-300";
          return (
            <div key={c.category_code || c.category_name}
                 className="border border-neutral-200 rounded-lg p-3"
                 data-testid={`pa-cat-${c.category_code}`}>
              <div className="flex items-baseline justify-between gap-2">
                <div className="font-bold text-sm">{c.category_name}</div>
                <div className="text-xs font-mono">{c.owned + c.duplicate}/{c.total}</div>
              </div>
              <div className="text-xs text-[var(--smoke)]">{pct}% · {c.remaining} remaining</div>
              <div className="h-1.5 mt-2 bg-neutral-200 rounded-full overflow-hidden">
                <div className={"h-full " + bar} style={{width: `${Math.min(100, Math.max(0, pct))}%`}} />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function OverviewTab({ completion }) {
  return (
    <section className="border-2 border-[var(--ink)] rounded-2xl bg-white p-6 grid grid-cols-1 md:grid-cols-2 gap-4"
             data-testid="pa-overview-tab">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)]">Progress at a glance</div>
        <ul className="text-sm mt-2 space-y-1">
          <li><b>{completion.owned}</b> owned</li>
          <li><b>{completion.duplicate}</b> duplicate</li>
          <li><b>{completion.missing}</b> missing</li>
          <li><b>{completion.unknown}</b> unknown</li>
          <li><b>{completion.remaining}</b> remaining to complete</li>
        </ul>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)]">Album total</div>
        <div className="font-display text-4xl mt-2">{completion.total}</div>
        <div className="text-xs text-[var(--smoke)]">official checklist stickers</div>
      </div>
    </section>
  );
}

function ListTab({ testid, title, rows, emptyText, q, setQ, catOptions,
                    catFilter, setCatFilter, onCopy, copyLabel, renderExtra }) {
  return (
    <section className="border-2 border-[var(--ink)] rounded-2xl bg-white overflow-hidden"
             data-testid={testid}>
      <header className="px-4 py-3 flex flex-wrap items-center gap-2 border-b border-neutral-200 bg-neutral-50">
        <div className="text-sm font-bold">{title}</div>
        <div className="ml-auto flex flex-wrap gap-2 items-center">
          <div className="relative">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--smoke)]" />
            <input value={q} onChange={e => setQ(e.target.value)}
                   placeholder="Search"
                   className="border-2 border-[var(--ink)] rounded-lg pl-8 pr-3 py-1.5 text-sm w-48"
                   data-testid={`${testid}-search`} />
          </div>
          <select value={catFilter} onChange={e => setCatFilter(e.target.value)}
                  className="border-2 border-[var(--ink)] rounded-lg px-3 py-1.5 text-sm bg-white"
                  data-testid={`${testid}-cat-filter`}>
            <option value="">All categories</option>
            {catOptions.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <button onClick={onCopy}
                  disabled={rows.length === 0}
                  className="border-2 border-[var(--ink)] rounded-full px-3 py-1.5 text-xs font-bold uppercase tracking-widest bg-white hover:bg-[var(--panini-yellow-soft)] flex items-center gap-1 disabled:opacity-50"
                  data-testid={`${testid}-copy`}>
            <Copy size={12} /> {copyLabel}
          </button>
        </div>
      </header>
      {rows.length === 0 ? (
        <div className="p-10 text-center text-[var(--smoke)] text-sm"
             data-testid={`${testid}-empty`}>
          {emptyText}
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest bg-neutral-50">
            <tr>
              <th className="text-left px-3 py-1.5">Code</th>
              <th className="text-left px-3 py-1.5">Name</th>
              <th className="text-left px-3 py-1.5">Category</th>
              {renderExtra && <th className="text-right px-3 py-1.5">Extra</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.sticker_number || i} className="border-t border-neutral-200"
                  data-testid={`${testid}-row-${r.sticker_number}`}>
                <td className="px-3 py-1.5 font-mono font-bold">{r.sticker_number}</td>
                <td className="px-3 py-1.5">
                  <div>{r.name}</div>
                  {(r.player_first_name || r.player_last_name) && (
                    <div className="text-xs text-[var(--smoke)]">
                      {[r.player_first_name, r.player_last_name].filter(Boolean).join(" ")}
                      {r.player_position ? ` · ${r.player_position}` : ""}
                    </div>
                  )}
                </td>
                <td className="px-3 py-1.5 text-xs font-mono">{r.category?.code || "—"}</td>
                {renderExtra && (
                  <td className="px-3 py-1.5 text-right font-mono">
                    +{r.extra_quantity || 0}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function TinyStat({ label, value, tone, testid }) {
  return (
    <div className={"border-2 rounded-lg text-center py-2 " + (TS_TONE[tone] || "border-neutral-300 bg-white")}
         data-testid={testid}>
      <div className="text-2xl font-display" data-testid={`${testid}-value`}>{value ?? 0}</div>
      <div className="text-[10px] uppercase tracking-widest">{label}</div>
    </div>
  );
}
function Meta({ k, v }) {
  return (
    <>
      <dt className="text-[10px] uppercase tracking-widest text-[var(--smoke)]">{k}</dt>
      <dd className="font-medium">{v ?? "—"}</dd>
    </>
  );
}
function NotFound({ message }) {
  return (
    <div className="min-h-screen grid place-items-center bg-[var(--cream)] p-6"
         data-testid="pa-not-found">
      <div className="max-w-md text-center">
        <div className="font-display text-3xl mb-2">Album not found</div>
        <p className="text-sm text-[var(--smoke)]">
          {message || "This album is private, archived, or the link is incorrect."}
        </p>
        <Link to="/" className="btn-primary inline-block mt-6" data-testid="pa-home">
          Back to CollectorUp
        </Link>
      </div>
    </div>
  );
}
